import { createContext, Dispatch, FC, ReactNode, useContext, useEffect, useReducer, useRef } from "react";
import { ExportFormatType, HSDFormatType, ExportGapFilterType, NetworkProviderFilterType, ExportProviderAvailabilityType } from "@app/exports/enumerations";
import { ProjectService } from "@app/projects";
import { ChangedGapsEnum, NetworkAssessmentAnalysisType, ProviderNetworkType } from "@app/projects/enumerators";
import { DropdownItem } from "@app/shared/checkbox-dropdown";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { useLocation, useRouteMatch } from "react-router-dom";
import { ColumnConfigurationPresetOption } from "./interfaces/columnConfigurationPresetOption";
import { ReportCustomizationRunConfiguration } from "./interfaces/reportCustomizationProjectRunConfiguration";
import { ReportCustomizationRouteState, ReportMembership } from "./interfaces/reportCustomizationRouteState";
import { getDefaultFileFormat, getDefaultReportName, getDefaultReportNamePrefixByReportType } from "./helpers";
import { toast } from "react-toastify";
import { formatDateTime } from "@app/shared/formatDateTime";
import startCase from "lodash/startCase";
import ExportService from "@app/exports/exportService";
import { ReportCustomizationState } from "./interfaces/reportCustomizationState";
import { reportCustomizationReducer } from "./reportCustomizationReducer";
import { ReportCustomizationReducerAction } from "./interfaces/reportCustomizationReducerAction";
import { ReportCustomizationReducerActions } from "./interfaces/reportCustomizationReducerActionsEnum";
import { ReportType } from "./interfaces/reportTypeEnum";
import { Roles, UserContext } from "@app/user";
import NetNewServicingProviderService from "@app/projects/servicing-provider/NetNewServicingProviderService";
import { defaultAllColumns } from "./defaultPresetValue";

interface ReportCustomizationRouteParams {
  isLatestPublished: string;
  module: string;
  projectId: string;
  runConfigurationId: string;
}

interface ReportCustomizationIncomingProps {
  children: ReactNode;
}

export interface ReportCustomizationOutgoingProps {
  changeChangedGapsType: (newChangedGaps: ChangedGapsEnum | "") => void,
  changeFileFormat: (newFileFormat: ExportFormatType) => void,
  changeIsCompareMode: (newIsCompareMode: boolean) => void,
  changeLoadedPresets: () => void,
  changeMemberships: (newMemberships: DropdownItem<number>[]) => void,
  changeProviderSource: (newProviderSource: ProviderNetworkType) => void,
  changeReportFormat: (newReportFormat: HSDFormatType) => void,
  changeReportType: (newReportType: ReportType) => void;
  changeTimeslice: (newTimeslice: string, isCompared: boolean) => void;
  dispatch: Dispatch<ReportCustomizationReducerAction>;
  excelRecordsLimit: number,
  getComparedProjectRunConfigurationId: () => number | undefined,
  maxExcelRecords: number,
  nonRecordRows: number,
  routeParams: ReportCustomizationRouteParams,
  routeState: ReportCustomizationRouteState,
  state: ReportCustomizationState;
  validateSettings: () => boolean;
}

export const ReportCustomizationContext = createContext<ReportCustomizationOutgoingProps>({} as ReportCustomizationOutgoingProps);

const maxExcelRecords = 1000000; // MS Excel sheet row limit is 1,048,576
const nonRecordRows = 50; // buffer for header, footer
const excelRecordsLimit = maxExcelRecords - nonRecordRows;

const getInitialMemberships = (primary: ReportMembership, secondary: ReportMembership, selected: number[]) => {
  const selectedModels: DropdownItem<number>[] = [];
  selected.forEach((s) => {
    if (s === primary.id) {
      selectedModels.push({
        key: primary.id,
        name: primary.alias,
      });
    } else if (s === secondary.id) {
      selectedModels.push({
        key: secondary.id,
        name: secondary.alias,
      });
    }
  });
  return selectedModels;
}

const getInitialState = (routeState: ReportCustomizationRouteState): ReportCustomizationState => {
  const defaultMemberships = [{ key: routeState.primaryMembership.id, name: routeState.primaryMembership.alias }];
  if (!!routeState.secondaryMembership.id)
    defaultMemberships.push({ key: routeState.secondaryMembership.id, name: routeState.secondaryMembership.alias });

  const defaultReportType = routeState.defaultReportType || ReportType.AdequacyServicingProvider;

  return {
    allMemberships: defaultMemberships,
    allServiceAreaCounties: [],
    allServiceAreaStates: [],
    allSpecialties: [],
    allTimeslices: [],
    appendNextClosestProviders: false,
    appendPatternOfCareProviders: !!routeState.appendPatternOfCareProviders,
    changedGapsType: routeState.adequacyFilters?.changedGaps || "",
    comparedTimeslice: routeState.selectedComparedTimeslice?.toString() || "",
    fileFormat: getDefaultFileFormat(routeState.exportNetworkProviderCount > excelRecordsLimit, defaultReportType),
    filename: getDefaultReportName(
      getDefaultReportNamePrefixByReportType(
        (routeState.defaultReportType || ReportType.AdequacyServicingProvider),
        routeState.isCompareMode || routeState.selectedMemberships.length === 2,
        routeState.defaultProviderSource || ProviderNetworkType.Network
      )
    ),
    includeActive: !routeState.isViewingOpportunity,
    includeApproved: !routeState.isViewingOpportunity,
    includeCargo: true,
    includeExpandableSpecialties: false,
    includeFacilitySpecialties: true,
    includeOpportunity: routeState.isViewingOpportunity,
    includePending: !routeState.isViewingOpportunity,
    includePhysicianSpecialties: true,
    isCompareMode: routeState.isCompareMode,
    isGenerating: false,
    networkAssessmentAnalysisTypes: routeState.isViewingOpportunity ? [NetworkAssessmentAnalysisType.Opportunity] : [NetworkAssessmentAnalysisType.ServiceArea],
    presets: [],
    providerFilter: NetworkProviderFilterType.Available,
    providerSource: routeState.defaultProviderSource || ProviderNetworkType.Network,
    reportFormat: routeState.appendPatternOfCareProviders ? HSDFormatType.MA : HSDFormatType.Default,
    reportType: defaultReportType,
    selectedCounties: routeState.defaultReportType !== ReportType.StateSummary ? routeState.selectedRegionSetIds?.map((rsi) => rsi.toString()) || [] : [],
    selectedMemberships: getInitialMemberships(routeState.primaryMembership, routeState.secondaryMembership, routeState.selectedMemberships),
    selectedPreset: defaultAllColumns,
    selectedSpecialties: [],
    selectedStates: routeState.defaultReportType === ReportType.StateSummary ? routeState.selectedRegionSetIds?.map((rsi) => rsi.toString()) || [] : [],
    serviceAreaGapsType: ExportGapFilterType.All,
    sharedUsers: [],
    shouldEmailLink: true,
    showAddedProviders: routeState.showAddedProviders,
    showMatchedProviders: routeState.showMatchedProviders,
    showNetNewProviders: false,
    showRemovedProviders: routeState.showRemovedProviders,
    specialtyGapsType: ExportGapFilterType.All,
    timeslice: routeState.selectedTimeslice.toString(),
  }
}

const ReportCustomizationContextProvider: FC<ReportCustomizationIncomingProps> = (props: ReportCustomizationIncomingProps) => {
  const user = useContext(UserContext);
  const routeState = useLocation<ReportCustomizationRouteState>().state;
  const routeMatch = useRouteMatch<ReportCustomizationRouteParams>();
  const projectService = useRef(new ProjectService()).current;
  const exportService = useRef(new ExportService()).current;
  const netNewServicingProviderService = useRef(new NetNewServicingProviderService()).current;
  const projectId = parseInt(routeMatch.params.projectId);

  const statesMap = useRef(new Map<string, DropdownItem<string>[]>());
  const countiesMap = useRef(new Map<string, (NestingParentDropdownItem)[]>());
  const specialtiesMap = useRef(new Map<string, Array<DropdownItem<string>>>());

  const [state, dispatch] = useReducer(reportCustomizationReducer, getInitialState(routeState));

  const defaultValues = useRef<ReportCustomizationState>({
    allMemberships: state.allMemberships,
    allServiceAreaCounties: state.allServiceAreaCounties,
    allServiceAreaStates: state.allServiceAreaStates,
    allSpecialties: state.allSpecialties,
    allTimeslices: state.allTimeslices,
    appendNextClosestProviders: state.appendNextClosestProviders,
    appendPatternOfCareProviders: state.appendPatternOfCareProviders,
    changedGapsType: "",
    comparedTimeslice: state.comparedTimeslice,
    fileFormat: state.fileFormat,
    filename: state.filename,
    includeActive: state.includeActive,
    includeApproved: state.includeApproved,
    includeCargo: state.includeCargo,
    includeExpandableSpecialties: state.includeExpandableSpecialties,
    includeFacilitySpecialties: state.includeFacilitySpecialties,
    includeOpportunity: state.includeOpportunity,
    includePending: state.includePending,
    includePhysicianSpecialties: state.includePhysicianSpecialties,
    isCompareMode: state.isCompareMode,
    isGenerating: state.isGenerating,
    networkAssessmentAnalysisTypes: state.networkAssessmentAnalysisTypes,
    presets: state.presets,
    providerFilter: state.providerFilter,
    providerSource: state.providerSource,
    reportFormat: state.reportFormat,
    reportType: state.reportType,
    selectedCounties: state.selectedCounties,
    selectedMemberships: state.selectedMemberships,
    selectedPreset: state.selectedPreset,
    selectedSpecialties: state.selectedSpecialties,
    selectedStates: state.selectedStates,
    serviceAreaGapsType: state.serviceAreaGapsType,
    sharedUsers: state.sharedUsers,
    shouldEmailLink: state.shouldEmailLink,
    showAddedProviders: state.showAddedProviders,
    showMatchedProviders: state.showMatchedProviders,
    showNetNewProviders: state.showNetNewProviders,
    showRemovedProviders: state.showRemovedProviders,
    specialtyGapsType: state.specialtyGapsType,
    timeslice: state.timeslice,
  }).current;

  const changeReportType = async (newReportType: ReportType) => {
    const prevReportType = state.reportType;
    const newFilename = getNewFilename(newReportType);
    const newFileFormat = getDefaultFileFormat(routeState.exportNetworkProviderCount > excelRecordsLimit, newReportType);
    const changeFileFormatSideEffects = getChangeFileFormatSideEffects(newFileFormat);

    let newState: Partial<ReportCustomizationState> = {
      ...state,
      fileFormat: newFileFormat,
      ...changeFileFormatSideEffects,
    };

    const { combinedServiceAreas: newServiceArea, newSelectedServiceAreas } = newReportType === ReportType.StateSummary ? await requestStates() : await requestCounties();
    switch (newReportType) {
      case ReportType.AdequacyServicingProvider:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          allServiceAreaCounties: newServiceArea as NestingParentDropdownItem[],
          changedGapsType: "",
          filename: newFilename,
          selectedCounties: newSelectedServiceAreas,
        };
        break;
      case ReportType.SpecialtySummaryByCounty:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          allServiceAreaCounties: newServiceArea as NestingParentDropdownItem[],
          filename: newFilename,
          selectedCounties: newSelectedServiceAreas,
        };
        break;
      case ReportType.ZipSummary:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getChangeReportFormatSideEffects(defaultValues.reportFormat),
          ...getProviderSectionDefaults(),
          allServiceAreaCounties: newServiceArea as NestingParentDropdownItem[],
          changedGapsType: "",
          includeCargo: defaultValues.includeCargo,
          selectedCounties: newSelectedServiceAreas,
        };
        break;
      case ReportType.StateSummary:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getChangeReportFormatSideEffects(defaultValues.reportFormat),
          changedGapsType: "",
          includeCargo: defaultValues.includeCargo,
          reportFormat: defaultValues.reportFormat,
          ...getSpecialtySectionDefaults(),
          ...getProviderSectionDefaults(),
          allServiceAreaStates: newServiceArea as DropdownItem<string>[],
          selectedStates: newSelectedServiceAreas,
        };
        break;
      case ReportType.CountySummary:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getChangeReportFormatSideEffects(defaultValues.reportFormat),
          changedGapsType: "",
          includeCargo: defaultValues.includeCargo,
          reportFormat: defaultValues.reportFormat,
          ...getSpecialtySectionDefaults(),
          ...getProviderSectionDefaults(),
          allServiceAreaCounties: newServiceArea as NestingParentDropdownItem[],
          selectedCounties: newSelectedServiceAreas,
        };
        break;
      case ReportType.AdequacySpecialtySummary:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getChangeReportFormatSideEffects(defaultValues.reportFormat),
          changedGapsType: "",
          includeCargo: defaultValues.includeCargo,
          reportFormat: defaultValues.reportFormat,
          ...getProviderSectionDefaults(),
          allServiceAreaCounties: newServiceArea as NestingParentDropdownItem[],
          selectedCounties: newSelectedServiceAreas,
        };
        break;
      case ReportType.AdequacyAggregate:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getChangeReportFormatSideEffects(defaultValues.reportFormat),
          changedGapsType: "",
          includeCargo: defaultValues.includeCargo,
          ...getProviderSectionDefaults(),
          // partial service area reset
          selectedCounties: defaultValues.selectedCounties,
          selectedStates: defaultValues.selectedStates,
          serviceAreaGapsType: defaultValues.serviceAreaGapsType,
        };
        break;
      case ReportType.AdequacyNextClosestProvider:
        newState = {
          ...newState,
          ...getChangeIsCompareModeSideEffects(false),
          changedGapsType: "",
          isCompareMode: false,
          ...getServiceAreaSectionDefaults(),
          ...getSpecialtySectionDefaults(),
          ...getProviderSectionDefaults(),
        }
        break;
      default:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getServiceAreaSectionDefaults(),
          ...getSpecialtySectionDefaults(),
          ...getProviderSectionDefaults(),
        }
        break;
    }

    const newPresets = await requestPresets(newReportType);
    const newSelectedPreset = defaultValues.selectedPreset;

    dispatch({
      payload: {
        ...newState,
        filename: newFilename,
        presets: newPresets,
        reportType: newReportType,
        selectedPreset: newSelectedPreset,
      },
      type: ReportCustomizationReducerActions.ChangeReportType,
    });
  }

  const changeTimeslice = async (newTimeslice: string, isCompared: boolean) => {
    let primaryTimeslice: string;
    let secondaryTimeslice: string;

    if (isCompared) {
      primaryTimeslice = state.timeslice;
      secondaryTimeslice = newTimeslice;
    } else {
      primaryTimeslice = newTimeslice;
      secondaryTimeslice = state.comparedTimeslice;
    }

    dispatch({
      payload: {
        ...(await getChangeTimesliceSideEffects(primaryTimeslice, secondaryTimeslice)),
        comparedTimeslice: secondaryTimeslice,
        timeslice: primaryTimeslice,
      },
      type: isCompared ? ReportCustomizationReducerActions.ChangeComparedTimeslice : ReportCustomizationReducerActions.ChangeTimeslice,
    })
  }

  const getChangeTimesliceSideEffects = async (primaryTimeslice: string, secondaryTimeslice: string): Promise<Partial<ReportCustomizationState>> => {
    const primaryConfig = state.allTimeslices.find((t) => t.projectRunConfigurationId.toString() === primaryTimeslice);
    const secondaryConfig = state.allTimeslices.find((t) => t.projectRunConfigurationId.toString() === secondaryTimeslice);
    const newAllMemberships = [{ key: primaryConfig.primaryMemberSetId, name: primaryConfig.primaryMemberSetAlias || primaryConfig.primaryMemberSetName }];
    if (!!primaryConfig.secondaryMemberSetId) {
      newAllMemberships.push({ key: primaryConfig.secondaryMemberSetId, name: primaryConfig.secondaryMemberSetAlias || primaryConfig.secondaryMemberSetName });
    }

    const isStateSummary = state.reportType === ReportType.StateSummary;
    let newAllServiceAreas = isStateSummary ? state.allServiceAreaStates : state.allServiceAreaCounties;
    let allServiceAreaIds: string[] = [];
    let newAllSpecialties = state.allSpecialties;
    // filter the selected service areas and specialties to ones that still exist in the new options
    let newSelectedServiceAreas: string[] = [];

    const newSelectedSpecialties = state.selectedSpecialties.filter((p) => newAllSpecialties.find((cs) => cs.key === p.key));

    // for primary timeslice changes, make sure the selected membership is still valid
    let newSelectedMemberships: DropdownItem<number>[] = [];
    let newIsCompareMode = state.isCompareMode;
    let newComparedTimeslice = secondaryTimeslice;
    let changeMembershipsSideEffects: Partial<ReportCustomizationState> = {};
    let changeIsCompareModeSideEffects: Partial<ReportCustomizationState> = {};

    if (primaryTimeslice !== state.timeslice) {
      state.selectedMemberships.forEach((m) => {
        if (m.key === primaryConfig.primaryMemberSetId || m.key === primaryConfig.secondaryMemberSetId) {
          newSelectedMemberships.push(m);
        }
      })
      // if there are no valid selections, make new ones
      if (newSelectedMemberships.length === 0) {
        newSelectedMemberships.push({ key: primaryConfig.primaryMemberSetId, name: primaryConfig.primaryMemberSetAlias || primaryConfig.primaryMemberSetName });
        if (routeState.selectedMemberships.length === 2 && !!primaryConfig.secondaryMemberSetId) {
          newSelectedMemberships.push({ key: primaryConfig.secondaryMemberSetId, name: primaryConfig.secondaryMemberSetAlias || primaryConfig.secondaryMemberSetName },);
        }
        changeMembershipsSideEffects = getChangeMembershipsSideEffects(newSelectedMemberships);
        // since there are new memberships, make sure the secondary timeslice (if any) is still valid
        if (!!secondaryTimeslice) {
          if (!newSelectedMemberships.every((m) => m.key === secondaryConfig.primaryMemberSetId || m.key === secondaryConfig.secondaryMemberSetId)) {
            newIsCompareMode = false;
            changeIsCompareModeSideEffects = getChangeIsCompareModeSideEffects(newIsCompareMode);
            newComparedTimeslice = "";
          }
        }
      } else {
        // if there are valid selections, still make sure the aliases/names are up to date
        // i.e. in case a primary membership is a secondary on a different timeslice
        newSelectedMemberships = newSelectedMemberships.map((m) => primaryConfig.primaryMemberSetId === m.key
          ? { key: m.key, name: primaryConfig.primaryMemberSetAlias || primaryConfig.primaryMemberSetName }
          : { key: m.key, name: primaryConfig.secondaryMemberSetAlias || primaryConfig.secondaryMemberSetName })
      }
    } else {
      newSelectedMemberships = state.selectedMemberships;
    }

    // retrieve updated service area and specialty options
    // async functions always return a promise, so the typing here is consistent
    // it's just ~instantaneous if it's in the Map already
    const promises: Promise<NestingParentDropdownItem | DropdownItem<string>[]>[] = [];
    if (isStateSummary) {
      promises.push(statesMap.current.has(primaryTimeslice) ? (async () => statesMap.current.get(primaryTimeslice))() : projectService.getStatesByRunConfigurationId(routeMatch.params.projectId, primaryTimeslice));
    } else {
      promises.push(countiesMap.current.has(primaryTimeslice) ? (async () => countiesMap.current.get(primaryTimeslice))() : projectService.getCountiesByRunConfigurationId(routeMatch.params.projectId, primaryTimeslice));
    }
    promises.push(specialtiesMap.current.has(primaryTimeslice) ? (async () => specialtiesMap.current.get(primaryTimeslice))() : projectService.getSpecialtiesByRunConfigurationId(routeMatch.params.projectId, primaryTimeslice));

    if (state.isCompareMode && !!secondaryTimeslice) {
      if (isStateSummary) {
        promises.push(statesMap.current.has(secondaryTimeslice) ? (async () => countiesMap.current.get(primaryTimeslice))() : projectService.getStatesByRunConfigurationId(routeMatch.params.projectId, secondaryTimeslice));
      } else {
        promises.push(countiesMap.current.has(secondaryTimeslice) ? (async () => countiesMap.current.get(secondaryTimeslice))() : projectService.getCountiesByRunConfigurationId(routeMatch.params.projectId, secondaryTimeslice));
      }
      promises.push(specialtiesMap.current.has(secondaryTimeslice) ? (async () => specialtiesMap.current.get(secondaryTimeslice))() : projectService.getSpecialtiesByRunConfigurationId(routeMatch.params.projectId, secondaryTimeslice));

      const [newPrimaryServiceArea, newPrimarySpecialties, newSecondaryServiceArea, newSecondarySpecialties] = await Promise.all(promises);

      if (isStateSummary) {
        statesMap.current.set(primaryTimeslice, newPrimaryServiceArea as DropdownItem<string>[]);
        statesMap.current.set(secondaryTimeslice, newSecondaryServiceArea as DropdownItem<string>[]);
        newAllServiceAreas = intersectStates(newPrimaryServiceArea as DropdownItem<string>[], newSecondaryServiceArea as DropdownItem<string>[]);
        allServiceAreaIds = newAllServiceAreas.map((csa) => csa.key);
        newSelectedServiceAreas = state.selectedStates.filter((p) => allServiceAreaIds.includes(p));
      } else {
        countiesMap.current.set(primaryTimeslice, newPrimaryServiceArea as NestingParentDropdownItem[]);
        countiesMap.current.set(secondaryTimeslice, newSecondaryServiceArea as NestingParentDropdownItem[]);
        newAllServiceAreas = intersectCounties(newPrimaryServiceArea as NestingParentDropdownItem[], newSecondaryServiceArea as NestingParentDropdownItem[]);
        allServiceAreaIds = (newAllServiceAreas as NestingParentDropdownItem[]).flatMap((csa) => csa.children.map((c) => c.key));
        newSelectedServiceAreas = state.selectedCounties.filter((p) => allServiceAreaIds.includes(p));
      }
      specialtiesMap.current.set(primaryTimeslice, newPrimarySpecialties as DropdownItem<string>[]);
      specialtiesMap.current.set(secondaryTimeslice, newSecondarySpecialties as DropdownItem<string>[]);

      newAllSpecialties = intersectSpecialties(newPrimarySpecialties as DropdownItem<string>[], newSecondarySpecialties as DropdownItem<string>[]);
    } else {
      const [newServiceAreas, newSpecialties] = await Promise.all(promises);
      if (isStateSummary) {
        statesMap.current.set(primaryTimeslice, newServiceAreas as DropdownItem<string>[]);
        allServiceAreaIds = (newServiceAreas as DropdownItem<string>[]).map((csa) => csa.key);
        newSelectedServiceAreas = state.selectedStates.filter((p) => allServiceAreaIds.includes(p));
      } else {
        countiesMap.current.set(primaryTimeslice, newServiceAreas as NestingParentDropdownItem[]);
        allServiceAreaIds = (newServiceAreas as NestingParentDropdownItem[]).flatMap((csa) => csa.children.map((c) => c.key));
        newSelectedServiceAreas = state.selectedCounties.filter((p) => allServiceAreaIds.includes(p));
      }
      newAllServiceAreas = newServiceAreas as NestingParentDropdownItem[];
      specialtiesMap.current.set(primaryTimeslice, newSpecialties as DropdownItem<string>[]);
      newAllSpecialties = newSpecialties as DropdownItem<string>[];
    }

    return {
      ...changeMembershipsSideEffects,
      ...changeIsCompareModeSideEffects,
      allMemberships: newAllMemberships,
      allServiceAreaCounties: isStateSummary ? state.allServiceAreaCounties : newAllServiceAreas as NestingParentDropdownItem[],
      allServiceAreaStates: isStateSummary ? newAllServiceAreas as DropdownItem<string>[] : state.allServiceAreaStates,
      allSpecialties: newAllSpecialties,
      changedGapsType: newIsCompareMode ? state.changedGapsType : "",
      comparedTimeslice: newComparedTimeslice,
      selectedCounties: isStateSummary ? state.selectedCounties : newSelectedServiceAreas,
      selectedMemberships: newSelectedMemberships,
      selectedSpecialties: newSelectedSpecialties,
      selectedStates: isStateSummary ? newSelectedServiceAreas : state.selectedStates,
    };
  }

  const changeFileFormat = (newFileFormat: ExportFormatType) => {
    const changeFileFormatSideEffects = getChangeFileFormatSideEffects(newFileFormat);

    dispatch({
      payload: {
        ...changeFileFormatSideEffects,
        fileFormat: newFileFormat,
      },
      type: ReportCustomizationReducerActions.ChangeFileFormat,
    })
  }

  const getChangeFileFormatSideEffects = (newFileFormat: ExportFormatType): Partial<ReportCustomizationState> => {
    let changeReportFormatSideEffects = {};
    let newReportFormat = state.reportFormat;
    if (newFileFormat === ExportFormatType.Excel || newFileFormat === ExportFormatType.Pipe) {
      newReportFormat = HSDFormatType.Default;
      changeReportFormatSideEffects = getChangeReportFormatSideEffects(HSDFormatType.Default);
    }
    return {
      ...changeReportFormatSideEffects,
      reportFormat: newReportFormat,
    };
  }

  const changeMemberships = (newMemberships: DropdownItem<number>[]) => {
    const changeMembershipsSideEffects = getChangeMembershipsSideEffects(newMemberships);

    dispatch({
      payload: {
        ...changeMembershipsSideEffects,
        selectedMemberships: newMemberships || [],
      },
      type: ReportCustomizationReducerActions.ChangeMemberships,
    })
  }

  const getChangeMembershipsSideEffects = (newMemberships: DropdownItem<number>[]): Partial<ReportCustomizationState> => {
    const newFilename = getNewFilename(undefined, undefined, newMemberships);
    const isTwoMemberships = newMemberships.length === 2;
    let changeIsCompareModeSideEffects = {};
    let changeProviderSourceSideEffects = {};
    let newProviderSource = state.providerSource;
    let newIsCompareMode = state.isCompareMode;
    if (newMemberships.length === 2) {
      newIsCompareMode = false;
      changeIsCompareModeSideEffects = getChangeIsCompareModeSideEffects(false);
      newProviderSource = ProviderNetworkType.Network;
      changeProviderSourceSideEffects = getChangeProviderSourceSideEffects(ProviderNetworkType.Network);
    }

    return {
      ...changeIsCompareModeSideEffects,
      ...changeProviderSourceSideEffects,
      filename: newFilename,
      isCompareMode: newIsCompareMode,
      providerSource: newProviderSource,
      showAddedProviders: isTwoMemberships,
      showMatchedProviders: isTwoMemberships,
      showRemovedProviders: isTwoMemberships,
    };
  }

  const changeIsCompareMode = (newIsCompareMode: boolean) => {
    const changeIsCompareModeSideEffects = getChangeIsCompareModeSideEffects(newIsCompareMode);
    dispatch({
      payload: {
        ...changeIsCompareModeSideEffects,
        isCompareMode: newIsCompareMode,
      },
      type: ReportCustomizationReducerActions.ChangeIsCompareMode,
    });
    // hard check false here, because it'll be undefined if newIsCompareMode is false
    if (state.appendPatternOfCareProviders && changeIsCompareModeSideEffects.appendPatternOfCareProviders === false) {
      renderPocUncheckedToast();
    }
  };

  const getChangeIsCompareModeSideEffects = (newIsCompareMode: boolean): Partial<ReportCustomizationState> => {
    let newMemberships = state.selectedMemberships;
    let newComparedTimeslice = state.comparedTimeslice;
    let newChangedGapsType = state.changedGapsType;
    let newProviderSource = state.providerSource;
    const primaryConfig = state.allTimeslices.find((t) => t.projectRunConfigurationId.toString() === state.timeslice);
    let providerSourceSideEffects: Partial<ReportCustomizationState> = {};

    if (newIsCompareMode) {
      newMemberships = state.selectedMemberships.length > 0
        ? [state.selectedMemberships[0]]
        : [{
          key: primaryConfig.primaryMemberSetId,
          name: primaryConfig.primaryMemberSetAlias || primaryConfig.primaryMemberSetName,
        }];
      newProviderSource = ProviderNetworkType.Network;
      providerSourceSideEffects = getChangeProviderSourceSideEffects(ProviderNetworkType.Network);

      // we need the latest published non-projected/termed timeslice that
      // is also not the selected primary timeslice and also shares a membership with the primary timeslice
      const eligibleComparedTimeslices = state.allTimeslices
        .filter((ts) => (
          ts.name !== "Projected"
          && ts.name !== "Termed"
          && ts.name !== "Projected-Termed"
          && ts.name !== "Unpublished"
          && ts.projectRunConfigurationId.toString() !== state.timeslice
          && (newMemberships[0].key === ts.primaryMemberSetId
            || newMemberships[0].key === ts.secondaryMemberSetId)));

      const latestEligibleTimestamp = Math.max(...eligibleComparedTimeslices.map((ts) => new Date(ts.dateCreated).getTime()));
      newComparedTimeslice = eligibleComparedTimeslices.find((ts) => new Date(ts.dateCreated).getTime() === latestEligibleTimestamp)?.projectRunConfigurationId.toString() || "";
    } else {
      newComparedTimeslice = "";
      newChangedGapsType = "";
    }

    const newFilename = getNewFilename(undefined, newIsCompareMode);

    return {
      ...providerSourceSideEffects,
      changedGapsType: newChangedGapsType,
      comparedTimeslice: newComparedTimeslice,
      filename: newFilename,
      providerSource: newProviderSource,
      selectedMemberships: newMemberships,
      showAddedProviders: newIsCompareMode,
      showMatchedProviders: newIsCompareMode,
      showRemovedProviders: newIsCompareMode,
    };
  }

  const changeProviderSource = (newProviderSource: ProviderNetworkType) => {
    const sideEffects = getChangeProviderSourceSideEffects(newProviderSource);
    dispatch({
      payload: {
        ...sideEffects,
        providerSource: newProviderSource,
      },
      type: ReportCustomizationReducerActions.ChangeProviderSource,
    });
    if (state.appendPatternOfCareProviders && !sideEffects.appendPatternOfCareProviders) {
      renderPocUncheckedToast();
    }
  }

  const getChangeProviderSourceSideEffects = (newProviderSource: ProviderNetworkType): Partial<ReportCustomizationState> => {
    const newFilename = getDefaultReportName(startCase(newProviderSource));
    let newProviderFilter = undefined as NetworkProviderFilterType | ExportProviderAvailabilityType;
    let newReportFormat = state.reportFormat;
    if (newProviderSource === ProviderNetworkType.Network) {
      newProviderFilter = NetworkProviderFilterType.Available;
    } else if (newProviderSource === ProviderNetworkType.Prospect || newProviderSource === ProviderNetworkType.Market) {
      newProviderFilter = ExportProviderAvailabilityType.All;
    }

    if (state.reportFormat === HSDFormatType.DISTINCT_SERVICING_PROVIDER_REPORT
      && newProviderSource !== ProviderNetworkType.Network
      && newProviderSource !== ProviderNetworkType.Prospect
      && newProviderSource !== ProviderNetworkType.Market) {
        newReportFormat = HSDFormatType.Default;
    }

    return {
      appendNextClosestProviders: false,
      appendPatternOfCareProviders: false,
      filename: newFilename,
      providerFilter: newProviderFilter,
      reportFormat: newReportFormat,
    }
  }

  const changeReportFormat = (newReportFormat: HSDFormatType) => {
    const changeReportFormatSideEffects = getChangeReportFormatSideEffects(newReportFormat);

    dispatch({
      payload: {
        ...changeReportFormatSideEffects,
        reportFormat: newReportFormat,
      },
      type: ReportCustomizationReducerActions.ChangeReportFormat,
    });
    if (state.appendPatternOfCareProviders && !changeReportFormatSideEffects.appendPatternOfCareProviders) {
      renderPocUncheckedToast();
    }
  }

  const getChangeReportFormatSideEffects = (newReportFormat: HSDFormatType): Partial<ReportCustomizationState> => {
    let newPatternOfCare = state.appendPatternOfCareProviders;
    // assuming that if appendPatternOfCareProviders is true, the user has the proper roles and such
    if (state.appendPatternOfCareProviders
      && newReportFormat !== HSDFormatType.MA && newReportFormat !== HSDFormatType.MMP) {
      newPatternOfCare = false;
    }
    return {
      appendPatternOfCareProviders: newPatternOfCare,
    };
  }

  // if the changed gaps filter changes
  const changeChangedGapsType = (newChangedGaps: ChangedGapsEnum | "") => {
    // the reducer is expecting values for these either way, so something must be provided
    const resets = newChangedGaps === "" ? {
      includeActive: state.includeActive,
      includeApproved: state.includeApproved,
      includeFacilitySpecialties: state.includeFacilitySpecialties,
      includeOpportunity: state.includeOpportunity,
      includePending: state.includePending,
      includePhysicianSpecialties: state.includePhysicianSpecialties,
      selectedCounties: state.selectedCounties,
      selectedSpecialties: state.selectedSpecialties,
      selectedStates: state.selectedStates,
      serviceAreaGapsType: state.serviceAreaGapsType,
      specialtyGapsType: state.specialtyGapsType,
    } : {
      ...getServiceAreaSectionDefaults(),
      ...getSpecialtySectionDefaults(),
    };

    dispatch({
      payload: {
        ...resets,
        changedGapsType: newChangedGaps,
      },
      type: ReportCustomizationReducerActions.ChangeChangedGapsFilter,
    });
  }

  const renderPocUncheckedToast = () => {
    toast("\"Append Pattern of Care Providers\" was unchecked", {
      containerId: "z-index-1030",
      type: "info",
    });
  }

  const getNewFilename = (newReportType?: ReportType, newIsCompareMode?: boolean, newMemberships?: DropdownItem<number>[]) => {
    let prefix = "";
    const { isCompareMode, selectedMemberships, providerSource, reportType } = state;
    const isComparison = newIsCompareMode === true || (newIsCompareMode === undefined && isCompareMode)
      || newMemberships?.length === 2 || (newMemberships === undefined && selectedMemberships.length === 2);
    if (newReportType === ReportType.AdequacyServicingProvider) {
      prefix = startCase(providerSource);
      if (isComparison) {
        prefix += " Comparison";
      }
    } else {
      prefix = getDefaultReportNamePrefixByReportType(newReportType || reportType, isComparison);
    }
    const newFilename = getDefaultReportName(prefix);
    return newFilename;
  }

  // API Requests
  useEffect(() => {
    const promises: Promise<unknown>[] = [
      requestTimeslices(),
      requestSpecialtyGroups(),
      requestPresets(),
      requestNetNewStatus(),
    ];

    if (state.reportType === ReportType.StateSummary) {
      promises.push(requestStates());
    } else {
      promises.push(requestCounties());
    }
    Promise.all(promises).then(([timeslicesResponse, specialtiesResponse, presetsResponse, netNewResponse, serviceAreaResponse]) => {
      const { combinedServiceAreas, newSelectedServiceAreas } = serviceAreaResponse as { combinedServiceAreas: (NestingParentDropdownItem | DropdownItem<string>)[], newSelectedServiceAreas: string[] };
      const { combinedSpecialties, newSelectedSpecialties } = specialtiesResponse as { combinedSpecialties: DropdownItem<string>[], newSelectedSpecialties: DropdownItem<string>[] };
      const isStateSummary = state.reportType === ReportType.StateSummary;

      const payload: Partial<ReportCustomizationState> = {
        allServiceAreaCounties: isStateSummary ? state.allServiceAreaCounties : combinedServiceAreas as NestingParentDropdownItem[],
        allServiceAreaStates: isStateSummary ? combinedServiceAreas : state.allServiceAreaStates,
        allSpecialties: combinedSpecialties,
        allTimeslices: timeslicesResponse as ReportCustomizationRunConfiguration[],
        presets: presetsResponse as ColumnConfigurationPresetOption[],
        selectedCounties: state.reportType === ReportType.StateSummary ? [] : newSelectedServiceAreas,
        selectedSpecialties: newSelectedSpecialties,
        selectedStates: state.reportType === ReportType.StateSummary ? newSelectedServiceAreas : [],
        showNetNewProviders: netNewResponse as boolean,
      };

      dispatch({
        payload,
        type: ReportCustomizationReducerActions.LoadPage,
      });
    })
  }, []);

  const requestTimeslices = async (): Promise<ReportCustomizationRunConfiguration[]> => {
    const newTimeslices = await projectService.getTimeslicesByProjectId(projectId.toString());
    return newTimeslices.map((nt) => {
      return {
        ...nt,
        dateCreated: formatDateTime(nt.dateCreated),
      }
    });
  }

  const requestCounties = async (): Promise<{ combinedServiceAreas: NestingParentDropdownItem[], newSelectedServiceAreas: string[] }> => {
    if (state.allServiceAreaCounties.length > 0) {
      return { combinedServiceAreas: state.allServiceAreaCounties, newSelectedServiceAreas: state.selectedCounties };
    }
    const { comparedTimeslice, isCompareMode, timeslice } = state;
    const promises = [projectService.getCountiesByRunConfigurationId(routeMatch.params.projectId, timeslice)];
    if (isCompareMode && !!comparedTimeslice) {
      promises.push(projectService.getCountiesByRunConfigurationId(routeMatch.params.projectId, comparedTimeslice));
    }

    const [primaryServiceArea, secondaryServiceArea] = await Promise.all(promises);
    const combinedServiceAreas = intersectCounties(primaryServiceArea, secondaryServiceArea);
    let newSelectedServiceAreas: string[] = [];
    // this is only called on page load or when changing to not-state-summary, so the assumption is the default report type expects county region set ids
    if (routeState.selectedRegionSetIds?.length > 0) {
      newSelectedServiceAreas = routeState.selectedRegionSetIds.map((rsi) => rsi.toString());
    } else {
      const allIds = (combinedServiceAreas).flatMap((sa) => sa.children.map((c) => c.key));
      newSelectedServiceAreas = allIds;
    }
    defaultValues.selectedCounties = newSelectedServiceAreas;
    countiesMap.current.set(timeslice, primaryServiceArea);
    countiesMap.current.set(comparedTimeslice, secondaryServiceArea);
    return { combinedServiceAreas, newSelectedServiceAreas };
  }

  const requestStates = async (): Promise<{ combinedServiceAreas: DropdownItem<string>[], newSelectedServiceAreas: string[] }> => {
    if (state.allServiceAreaStates.length > 0) {
      return { combinedServiceAreas: state.allServiceAreaStates, newSelectedServiceAreas: state.selectedStates };
    }
    const { comparedTimeslice, isCompareMode, timeslice } = state;
    const promises = [projectService.getStatesByRunConfigurationId(routeMatch.params.projectId, timeslice)];
    if (isCompareMode && !!comparedTimeslice) {
      promises.push(projectService.getStatesByRunConfigurationId(routeMatch.params.projectId, comparedTimeslice));
    }

    const [primaryServiceArea, secondaryServiceArea] = await Promise.all(promises);
    const combinedServiceAreas = intersectStates(primaryServiceArea, secondaryServiceArea);
    let newSelectedServiceAreas: string[] = [];
    // this is only called on page load or when changing to state summary, so the assumption is the default report type expects state region set ids
    if (routeState.selectedRegionSetIds?.length > 0) {
      newSelectedServiceAreas = routeState.selectedRegionSetIds.map((rsi) => rsi.toString());
    } else {
      const allIds = combinedServiceAreas.map((sa) => sa.key);
      newSelectedServiceAreas = allIds;
    }
    defaultValues.selectedStates = newSelectedServiceAreas;
    statesMap.current.set(timeslice, primaryServiceArea);
    statesMap.current.set(comparedTimeslice, secondaryServiceArea);
    return { combinedServiceAreas, newSelectedServiceAreas };
  }

  const requestSpecialtyGroups = async (): Promise<{ combinedSpecialties: DropdownItem<string>[], newSelectedSpecialties: DropdownItem<string>[] }> => {
    const { comparedTimeslice, isCompareMode, timeslice } = state;
    const promises = [projectService.getSpecialtiesByRunConfigurationId(routeMatch.params.projectId, timeslice)];
    if (isCompareMode && !!comparedTimeslice) {
      promises.push(projectService.getSpecialtiesByRunConfigurationId(routeMatch.params.projectId, comparedTimeslice));
    }
    const [primarySpecialties, secondarySpecialties] = await Promise.all(promises);
    const combinedSpecialties = intersectSpecialties(primarySpecialties, secondarySpecialties);
    let newSelectedSpecialties: DropdownItem<string>[] = [];
    if (routeState.selectedSpecialties?.length > 0) {
      const matches = combinedSpecialties.filter((ns) => routeState.selectedSpecialties.includes(ns.key));
      newSelectedSpecialties = matches;
    } else {
      newSelectedSpecialties = combinedSpecialties;
    }
    defaultValues.selectedSpecialties = newSelectedSpecialties;
    specialtiesMap.current.set(timeslice, primarySpecialties);
    specialtiesMap.current.set(comparedTimeslice, secondarySpecialties);
    return { combinedSpecialties, newSelectedSpecialties };
  }

  const requestPresets = async (reportType: ReportType = state.reportType): Promise<ColumnConfigurationPresetOption[]> => {
    try {
      const apiPresets = await exportService.getColumnCustomizationPresets(reportType);
      return [defaultValues.selectedPreset, ...apiPresets];
    } catch {
      return [defaultValues.selectedPreset];
    }
  }

  const requestNetNewStatus = async () => {
    try {
      if (user.roles.includes(Roles.NET_NEW_PROVIDER_USER)) {
        return await netNewServicingProviderService.projectHasNetNewServicingProviders(parseInt(routeMatch.params.projectId, 10));
      }
    } catch {
      return false;
    }
  }

  const changeLoadedPresets = async () => {
    const updatedPresets = await requestPresets();
    dispatch({
      payload: { presets: updatedPresets },
      type: ReportCustomizationReducerActions.ChangeLoadedPresets,
    });
  };

  const intersectStates = (primaryServiceArea: DropdownItem<string>[], secondaryServiceArea: DropdownItem<string>[] = []) => {
    const combinedServiceAreas = primaryServiceArea;
    if (secondaryServiceArea?.length > 0) {
      secondaryServiceArea.forEach((sa) => {
        if (!combinedServiceAreas.find((ca) => ca.key === sa.key)) {
          combinedServiceAreas.push(sa);
        }
      })
    }
    return combinedServiceAreas.sort((a, b) => a.name > b.name ? 1 : -1);
  }

  const intersectCounties = (primaryServiceArea: NestingParentDropdownItem[], secondaryServiceArea: NestingParentDropdownItem[] = []) => {
    // see which states are in both timeslices, and then check the counties
    return (secondaryServiceArea.length > 0
      ? primaryServiceArea.reduce((result, current) => {
        const secondaryStateMatch = secondaryServiceArea.find((s) => s.key === current.key);
        if (!!secondaryStateMatch) {
          const newState = current;
          newState.children = current.children.filter((c) => !!secondaryStateMatch.children.find((s) => s.key === c.key));
          result.push(newState);
        }
        return result;
      }, new Array<NestingParentDropdownItem>())
      : primaryServiceArea)
      .sort((a, b) => a.name > b.name ? 1 : -1)
      .map((state) => { state.children.sort((a, b) => a.name > b.name ? 1 : -1); return state; });
  }

  const intersectSpecialties = (primarySpecialties: DropdownItem<string>[], secondarySpecialties: DropdownItem<string>[] = []) => {
    return (secondarySpecialties.length > 0
      ? (primarySpecialties as DropdownItem<string>[]).filter((primary) => !!secondarySpecialties.find((secondary) => primary.key === secondary.key))
      : primarySpecialties)
      .sort((a, b) => a.order > b.order ? 1 : -1);
  }

  const resetCompareMode = async (prevReportType: ReportType) => {
    let newIsCompareMode = state.isCompareMode;
    let newComparedTimeslice = state.comparedTimeslice;
    let changeIsCompareModeSideEffects = {};
    let changeTimesliceSideEffects = {};

    // ClosestProvider does not allow compare mode
    if (prevReportType === ReportType.AdequacyNextClosestProvider) {
      newIsCompareMode = defaultValues.isCompareMode;
      changeIsCompareModeSideEffects = getChangeIsCompareModeSideEffects(newIsCompareMode);
      newComparedTimeslice = defaultValues.comparedTimeslice === state.timeslice
        ? state.allTimeslices.find((t) => t.projectRunConfigurationId.toString() !== state.timeslice).projectRunConfigurationId.toString()
        : defaultValues.comparedTimeslice;

      changeTimesliceSideEffects = await getChangeTimesliceSideEffects(state.timeslice, newComparedTimeslice);
    }
    return {
      ...changeIsCompareModeSideEffects,
      ...changeTimesliceSideEffects,
      newComparedTimeslice,
      newIsCompareMode,
    };
  }

  const getServiceAreaSectionDefaults = (): Partial<ReportCustomizationState> => {
    return {
      includeActive: defaultValues.includeActive,
      includeApproved: defaultValues.includeApproved,
      includeOpportunity: defaultValues.includeOpportunity,
      includePending: defaultValues.includePending,
      networkAssessmentAnalysisTypes: defaultValues.networkAssessmentAnalysisTypes,
      selectedCounties: defaultValues.selectedCounties,
      selectedStates: defaultValues.selectedStates,
      serviceAreaGapsType: defaultValues.serviceAreaGapsType,
    };
  }

  const getSpecialtySectionDefaults = (): Partial<ReportCustomizationState> => {
    return {
      includeFacilitySpecialties: defaultValues.includeFacilitySpecialties,
      includePhysicianSpecialties: defaultValues.includePhysicianSpecialties,
      selectedSpecialties: defaultValues.selectedSpecialties,
      specialtyGapsType: defaultValues.specialtyGapsType,
    };
  }

  const getProviderSectionDefaults = (): Partial<ReportCustomizationState> => {
    return {
      ...getChangeProviderSourceSideEffects(defaultValues.providerSource as ProviderNetworkType),
      appendNextClosestProviders: defaultValues.appendNextClosestProviders,
      appendPatternOfCareProviders: defaultValues.appendPatternOfCareProviders,
      providerFilter: defaultValues.providerFilter,
      providerSource: defaultValues.providerSource,
      showAddedProviders: defaultValues.showAddedProviders,
      showMatchedProviders: defaultValues.showMatchedProviders,
      showRemovedProviders: defaultValues.showRemovedProviders,
    };
  }

  const validateSettings = () => {
    return state.selectedMemberships.length > 0
      && !!state.timeslice && (!state.isCompareMode || (state.isCompareMode && !!state.comparedTimeslice))
      && !!state.filename
      && (state.includeActive || state.includePending || state.includeApproved || state.includeOpportunity);
  }

  const getComparedProjectRunConfigurationId = (): number | undefined => {
    const comparedProjectTimeslice = parseInt(state.comparedTimeslice);
    if (!isNaN(comparedProjectTimeslice)) {
      return comparedProjectTimeslice;
    }

    if (state.selectedMemberships.length < 2) {
      return undefined;
    }

    const projectionRunConfiguration = state.allTimeslices.find(slice => slice.projectRunConfigurationId === parseInt(state.timeslice));
    if (projectionRunConfiguration && projectionRunConfiguration.companionProjectRunConfigurationId) {
      return projectionRunConfiguration.companionProjectRunConfigurationId;
    }

    return undefined;
  }

  return (
    <ReportCustomizationContext.Provider
      value={{
        changeChangedGapsType,
        changeFileFormat,
        changeIsCompareMode,
        changeLoadedPresets,
        changeMemberships,
        changeProviderSource,
        changeReportFormat,
        changeReportType,
        changeTimeslice,
        dispatch,
        excelRecordsLimit,
        getComparedProjectRunConfigurationId,
        maxExcelRecords,
        nonRecordRows,
        routeParams: routeMatch.params,
        routeState,
        state,
        validateSettings,
      }}>
      {props.children}
    </ReportCustomizationContext.Provider>
  )
}

export default ReportCustomizationContextProvider;